import React, { useState, useEffect } from 'react';
import FirebaseImageUpload from '../components/FirebaseImageUpload';
import { callApi } from '../api/api';

const LabsView = ({ showPage, isAuthenticated, currentUser, addToCart, showMessage }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [testText, setTestText] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [landmark, setLandmark] = useState('');
    const [pincode, setPincode] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [isEditingDetails, setIsEditingDetails] = useState(false);
    const [uploadedUrls, setUploadedUrls] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [referredBy, setReferredBy] = useState('');
    const [patientAge, setPatientAge] = useState('');
    const [patientGender, setPatientGender] = useState('');
    
    // New state for lab search
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedTests, setSelectedTests] = useState([]);
    const [popularTests, setPopularTests] = useState([]);
    const [comprehensivePackages, setComprehensivePackages] = useState([]);

    // Fetch popular tests and comprehensive packages on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [popularRes, compRes] = await Promise.all([
                    callApi('/labs/popular', 'GET'),
                    callApi('/labs/comprehensive', 'GET')
                ]);
                
                if (popularRes.success) {
                    setPopularTests(popularRes.data || []);
                }
                if (compRes.success) {
                    setComprehensivePackages(compRes.data || []);
                }
            } catch (error) {
                console.error("Error fetching lab data:", error);
            }
        };
        fetchData();
    }, []);

    // Debounced search
    useEffect(() => {
        const fetchResults = async () => {
            if (searchQuery.trim().length < 2) {
                setSearchResults([]);
                return;
            }
            setIsSearching(true);
            try {
                const response = await callApi(`/labs/search?q=${encodeURIComponent(searchQuery)}`, 'GET');
                if (response.success) {
                    setSearchResults(response.data || []);
                }
            } catch (error) {
                console.error("Search error", error);
            } finally {
                setIsSearching(false);
            }
        };

        const timerId = setTimeout(() => {
            fetchResults();
        }, 300);

        return () => clearTimeout(timerId);
    }, [searchQuery]);

    React.useEffect(() => {
        if (isAuthenticated && currentUser) {
            setName(currentUser.name || '');
            setPhone(currentUser.mobile || '');

            let defaultAddress = currentUser.address || '';
            if (currentUser.addresses && currentUser.addresses.length > 0) {
                const addr = currentUser.addresses[0];
                defaultAddress = `${addr.line1}, ${addr.line2 ? addr.line2 + ', ' : ''}${addr.city}, ${addr.state}`;
                if (addr.pincode) setPincode(addr.pincode);
                if (addr.landmark) setLandmark(addr.landmark);
            }
            setAddress(defaultAddress);

            if (!currentUser.name || !currentUser.mobile || !defaultAddress) {
                setIsEditingDetails(true);
            } else {
                setIsEditingDetails(false);
            }
        } else {
            setIsEditingDetails(true);
        }
    }, [isAuthenticated, currentUser]);

    const handleSaveAndSend = async () => {
        if (!isAuthenticated) {
            alert('Please login first to submit a lab request.');
            return;
        }

        if (!name.trim() || !phone.trim() || !address.trim() || !landmark.trim() || !pincode.trim()) {
            alert('Name, mobile number, address, landmark, and pincode are required.');
            return;
        }

        if (uploadedUrls.length === 0 && testText.trim() === '') {
            alert('Please either upload a prescription or type the test names.');
            return;
        }

        setIsSubmitting(true);
        
        try {
            // Send the testText directly
            const combinedTestText = testText;
            
            const fullAddress = `${address}, Landmark: ${landmark}, Pincode: ${pincode}`;

            const response = await callApi('/labs/request', 'POST', {
                name: name,
                mobile: phone,
                address: fullAddress,
                testText: combinedTestText,
                images: uploadedUrls,
                referredBy: referredBy || undefined,
                patientAge: patientAge ? parseInt(patientAge) : undefined,
                patientGender: patientGender || undefined
            });

            if (response.success) {
                setUploadSuccess(true);
                setTestText('');
                setUploadedUrls([]);
                setSelectedTests([]);
                setSearchQuery('');
                alert('Lab request submitted successfully!');
            } else {
                alert('Failed to submit lab request. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting lab request:', error);
            alert('An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };
    return (
        <div className="min-h-screen bg-slate-50 pb-8 font-sans">
            {/* Hero Section with Dynamic Aesthetic Design */}
            <div className="relative pt-4 pb-12 sm:pt-6 sm:pb-20 px-3 sm:px-4 bg-gradient-to-b from-sky-50 via-white to-slate-50">
                {/* Animated Glowing Background Blobs */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-0 left-1/4 w-40 h-40 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-[80px] md:blur-[128px] opacity-70 animate-pulse"></div>
                    <div className="absolute top-0 right-1/4 w-40 h-40 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-sky-200 rounded-full mix-blend-multiply filter blur-[80px] md:blur-[128px] opacity-70 animate-pulse" style={{ animationDelay: '2s' }}></div>
                    <div className="absolute -bottom-32 left-1/2 w-40 h-40 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-emerald-100 rounded-full mix-blend-multiply filter blur-[80px] md:blur-[128px] opacity-70 animate-pulse" style={{ animationDelay: '4s' }}></div>
                </div>

                <div className="relative max-w-3xl mx-auto z-50 pb-2">
                    {/* Search Bar */}
                    <div className="relative shadow-lg rounded-full bg-white/60 p-1 md:p-2 backdrop-blur-xl border border-white transform transition-transform">
                        <div className="relative flex items-center bg-white rounded-full px-3 py-2.5 sm:px-4 sm:py-3 md:px-6 md:py-4 shadow-sm z-50">
                            <i className="fas fa-search text-slate-400 text-base sm:text-xl mr-2 sm:mr-4"></i>
                            <input 
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search tests (e.g. CBC, Thyroid)..."
                                className="flex-1 outline-none text-slate-800 font-medium text-sm sm:text-base md:text-lg bg-transparent placeholder-slate-400 min-w-0"
                            />
                            {isSearching && <i className="fas fa-spinner fa-spin text-sky-500 mr-4"></i>}
                        </div>
                        
                        {/* Search Results Dropdown */}
                        {searchResults.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-40 max-h-80 overflow-y-auto">
                                {searchResults.map((test) => (
                                    <div 
                                        key={test._id} 
                                        className="px-6 py-4 border-b border-slate-50 hover:bg-slate-50 cursor-pointer flex justify-between items-center transition-colors"
                                        onClick={() => {
                                            const testToAdd = {
                                                _id: test._id || 'test_' + test.testName,
                                                productName: test.testName,
                                                mrp: test.mrp,
                                                packSize: '1 Test',
                                                imageUrl: 'https://cdn-icons-png.flaticon.com/512/912/912300.png'
                                            };
                                            if (addToCart) addToCart(testToAdd);
                                            if (showMessage) showMessage(`${test.testName} added to cart!`);
                                            
                                            setSearchQuery('');
                                            setSearchResults([]);
                                        }}
                                    >
                                        <div>
                                            <h4 className="font-bold text-slate-800">{test.testName}</h4>
                                            {test.dealType && <span className="text-xs font-semibold text-sky-600 bg-sky-50 px-2 py-1 rounded-md mt-1 mb-1 inline-block">{test.dealType}</span>}
                                            {test.testsList && (
                                                <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                                    {test.testsList.split(' | ').join(', ')}
                                                </p>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-emerald-600">₹{test.mrp}</div>
                                            <button className="text-xs bg-slate-800 text-white px-3 py-1 rounded-lg mt-1 hover:bg-slate-700">Add</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* Main Content Area */}
            <div className="max-w-6xl mx-auto px-3 sm:px-4 mt-0">

                {/* Upload Prescription CTA (Simplified) */}
                <div className="mb-6 sm:mb-8 z-20 relative">
                    <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-12 text-slate-800 flex flex-col lg:flex-row items-center justify-between shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white relative overflow-hidden group gap-6 sm:gap-8 md:gap-12">
                        {/* Shimmer Effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:animate-[shimmer_2s_infinite]"></div>
                        
                        <div className="absolute top-0 right-0 opacity-[0.03] pointer-events-none transform translate-x-1/4 -translate-y-1/4 transition-transform duration-700 group-hover:rotate-12 group-hover:scale-110">
                            <i className="fas fa-microscope text-[16rem] text-indigo-900"></i>
                        </div>
                        
                        {/* Left Side: Form */}
                        <div className="relative z-10 w-full max-w-3xl mx-auto">
                            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-2 text-slate-800">Need a Lab Test?</h2>
                            <p className="text-slate-500 text-sm sm:text-base md:text-lg mb-5 sm:mb-8 leading-relaxed">
                                Upload your prescription or type the test names directly.
                            </p>
                            
                            {uploadSuccess ? (
                                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-2xl flex items-start gap-4 shadow-sm">
                                    <div className="mt-1">
                                        <i className="fas fa-check-circle text-4xl text-emerald-500 animate-bounce"></i>
                                    </div>
                                    <div className="text-left">
                                        <h4 className="font-bold text-xl text-emerald-700 mb-1">Request Received!</h4>
                                        <p className="text-sm text-emerald-600 leading-relaxed">Thank you! Our experts will review your details and contact you shortly.</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] text-left relative overflow-hidden">
                                    {!isEditingDetails && isAuthenticated && currentUser ? (
                                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-5 mb-5 sm:mb-8 relative z-10">
                                            <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                                                <h3 className="font-bold text-slate-800 text-base sm:text-lg flex items-center gap-2"><i className="fas fa-map-marker-alt text-sky-500"></i> Deliver To</h3>
                                                <button type="button" onClick={() => setIsEditingDetails(true)} className="text-sky-600 text-xs sm:text-sm font-bold hover:underline bg-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-xl border border-sky-200 shadow-sm transition-colors hover:bg-sky-50 flex items-center gap-1 sm:gap-2"><i className="fas fa-edit"></i> Change Details</button>
                                            </div>
                                            <p className="font-bold text-slate-800 mt-4">{name} <span className="text-slate-500 font-normal ml-2">{phone}</span></p>
                                            <p className="text-slate-600 mt-1">{address}</p>
                                            {(landmark || pincode) && <p className="text-slate-500 text-sm mt-1">{landmark && `Landmark: ${landmark}`}{landmark && pincode && ', '} {pincode && `Pincode: ${pincode}`}</p>}
                                        </div>
                                    ) : (
                                        <div className="bg-slate-50 p-4 sm:p-6 rounded-2xl border border-slate-200 mb-5 sm:mb-8 relative z-10">
                                            <div className="flex flex-wrap justify-between items-center gap-2 mb-4 sm:mb-6">
                                                <h3 className="font-bold text-slate-800 text-base sm:text-lg flex items-center gap-2"><i className="fas fa-address-card text-sky-500"></i> Contact & Delivery Details</h3>
                                                {isAuthenticated && currentUser && currentUser.name && currentUser.mobile && (
                                                    <button type="button" onClick={() => setIsEditingDetails(false)} className="text-slate-500 font-medium text-sm hover:text-slate-800 hover:bg-slate-200 bg-white border border-slate-300 px-3 py-1.5 rounded-lg transition-colors">Cancel Edit</button>
                                                )}
                                            </div>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div className="flex flex-col">
                                                    <label className="text-sm font-extrabold text-sky-600 uppercase tracking-widest mb-2">Patient's Full Name *</label>
                                                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your name" className="bg-white border border-slate-200 rounded-xl p-3 text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <label className="text-sm font-extrabold text-sky-600 uppercase tracking-widest mb-2">Phone Number *</label>
                                                    <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Enter your mobile number" className="bg-white border border-slate-200 rounded-xl p-3 text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all" />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                {/* Referred By */}
                                                <div className="flex flex-col">
                                                    <label className="text-sm font-extrabold text-violet-600 uppercase tracking-widest mb-2 flex items-center gap-1.5"><i className="fas fa-user-tag"></i> Referred By <span className="text-violet-400 font-normal normal-case tracking-normal"> (optional)</span></label>
                                                    <input type="text" value={referredBy} onChange={(e) => setReferredBy(e.target.value)} placeholder="Doctor or person who referred you" className="bg-white border border-violet-200 rounded-xl p-3 text-slate-800 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 transition-all" />
                                                </div>

                                                {/* Patient Age & Gender */}
                                                <div className="flex flex-col">
                                                    <label className="text-sm font-extrabold text-rose-600 uppercase tracking-widest mb-2 flex items-center gap-1.5"><i className="fas fa-user"></i> Patient Info <span className="text-rose-400 font-normal normal-case tracking-normal"> (optional)</span></label>
                                                    <div className="flex gap-3">
                                                        <input type="number" min="1" max="120" value={patientAge} onChange={(e) => setPatientAge(e.target.value)} placeholder="Age" className="w-24 bg-white border border-rose-200 rounded-xl p-3 text-slate-800 outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 transition-all font-bold text-center" />
                                                        <select value={patientGender} onChange={(e) => setPatientGender(e.target.value)} className="flex-1 bg-white border border-rose-200 rounded-xl p-3 text-slate-800 outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 transition-all font-semibold">
                                                            <option value="">Select Gender</option>
                                                            <option value="Male">Male</option>
                                                            <option value="Female">Female</option>
                                                            <option value="Other">Other</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <div className="flex flex-col">
                                                    <label className="text-sm font-extrabold text-sky-600 uppercase tracking-widest mb-2">Delivery Address *</label>
                                                    <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Enter your delivery address" className="bg-white border border-slate-200 rounded-xl p-3 text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all w-full" />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="flex flex-col">
                                                    <label className="text-sm font-extrabold text-sky-600 uppercase tracking-widest mb-2">Landmark *</label>
                                                    <input type="text" value={landmark} onChange={(e) => setLandmark(e.target.value)} placeholder="e.g. Near Apollo Hospital" className="bg-white border border-slate-200 rounded-xl p-3 text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <label className="text-sm font-extrabold text-sky-600 uppercase tracking-widest mb-2">Pincode *</label>
                                                    <input type="text" value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="e.g. 400001" className="bg-white border border-slate-200 rounded-xl p-3 text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all" />
                                                </div>
                                            </div>

                                        </div>
                                    )}
                                    
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mb-5 sm:mb-6 relative z-10">
                                        
                                        {/* Upload Option */}
                                        <div className="flex flex-col">
                                            <p className="text-sm font-extrabold text-sky-600 uppercase tracking-widest flex items-center gap-2 mb-3">
                                                <i className="fas fa-cloud-upload-alt"></i> Upload Option
                                            </p>
                                            <div className="flex-1 bg-slate-50/50 rounded-xl p-4 border border-slate-200">
                                                <FirebaseImageUpload 
                                                    onUploadSuccess={(urls) => setUploadedUrls(Array.isArray(urls) ? urls : [urls])} 
                                                    folder="lab_prescriptions"
                                                    allowMultiple={true}
                                                    maxFiles={3}
                                                />
                                            </div>
                                        </div>

                                        {/* Text Option */}
                                        <div className="flex flex-col">
                                            <p className="text-sm font-extrabold text-sky-600 uppercase tracking-widest flex items-center gap-2 mb-3">
                                                <i className="fas fa-keyboard"></i> Or Type Test Name
                                            </p>
                                            <textarea 
                                                value={testText}
                                                onChange={(e) => setTestText(e.target.value)}
                                                className="flex-1 w-full bg-slate-50/50 border border-slate-200 rounded-xl p-4 text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all placeholder-slate-400 resize-none min-h-[120px]"
                                                placeholder="e.g. CBC, Vitamin D..."
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 relative z-10">
                                        <button 
                                            onClick={handleSaveAndSend}
                                            disabled={isSubmitting}
                                            className="flex-1 bg-sky-600 text-white font-bold py-3 sm:py-4 rounded-xl shadow-lg hover:bg-sky-700 hover:shadow-xl transition-all flex items-center justify-center gap-2 text-base sm:text-lg disabled:opacity-50"
                                        >
                                            <i className={`fas ${isSubmitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'} text-xl`}></i> {isSubmitting ? 'Submitting...' : 'Submit'}
                                        </button>
                                        <button 
                                            onClick={() => window.open('https://wa.me/919029122955?text=Hi,%20I%20want%20to%20book%20a%20lab%20test', '_blank')}
                                            className="flex-1 bg-[#25D366] text-white font-bold py-3 sm:py-4 rounded-xl shadow-lg hover:bg-[#128C7E] hover:shadow-xl transition-all flex items-center justify-center gap-2 text-base sm:text-lg"
                                        >
                                            <i className="fab fa-whatsapp text-xl sm:text-2xl"></i> WhatsApp Us
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>



                {/* Features Grid (Shifted Down) */}
                <div className="relative max-w-6xl mx-auto z-10 mt-4 sm:mt-6 mb-10 sm:mb-16">
                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                        <div className="flex items-center gap-2 sm:gap-4 bg-white/80 backdrop-blur-md p-3 sm:p-5 rounded-xl sm:rounded-2xl border border-white shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 text-base sm:text-xl shrink-0 shadow-inner">
                                <i className="fas fa-certificate"></i>
                            </div>
                            <div>
                                <h4 className="font-extrabold text-slate-800 text-sm sm:text-lg leading-tight">NABL Certified</h4>
                                <p className="text-slate-500 text-xs sm:text-sm font-medium mt-0.5">100% Accurate</p>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-2 sm:gap-4 bg-white/80 backdrop-blur-md p-3 sm:p-5 rounded-xl sm:rounded-2xl border border-white shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-sky-50 flex items-center justify-center text-sky-500 text-base sm:text-xl shrink-0 shadow-inner">
                                <i className="fas fa-home"></i>
                            </div>
                            <div>
                                <h4 className="font-extrabold text-slate-800 text-sm sm:text-lg leading-tight">Free Collection</h4>
                                <p className="text-slate-500 text-xs sm:text-sm font-medium mt-0.5">From your home</p>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-2 sm:gap-4 bg-white/80 backdrop-blur-md p-3 sm:p-5 rounded-xl sm:rounded-2xl border border-white shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-500 text-base sm:text-xl shrink-0 shadow-inner">
                                <i className="fas fa-bolt"></i>
                            </div>
                            <div>
                                <h4 className="font-extrabold text-slate-800 text-sm sm:text-lg leading-tight">Fast Reports</h4>
                                <p className="text-slate-500 text-xs sm:text-sm font-medium mt-0.5">Within 24 hours</p>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-2 sm:gap-4 bg-white/80 backdrop-blur-md p-3 sm:p-5 rounded-xl sm:rounded-2xl border border-white shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-green-50 flex items-center justify-center text-green-500 text-xl sm:text-2xl shrink-0 shadow-inner">
                                <i className="fab fa-whatsapp"></i>
                            </div>
                            <div>
                                <h4 className="font-extrabold text-slate-800 text-sm sm:text-lg leading-tight">E-Reports</h4>
                                <p className="text-slate-500 text-xs sm:text-sm font-medium mt-0.5">Direct to WhatsApp</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Popular Tests Section */}
                {popularTests.length > 0 && (
                    <div className="mb-6 sm:mb-8">
                        <div className="flex justify-between items-end mb-4 sm:mb-6">
                            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Popular Tests</h2>
                            <button className="text-indigo-600 font-semibold hover:text-indigo-800 text-sm sm:text-base">View All <i className="fas fa-arrow-right text-xs sm:text-sm ml-1"></i></button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                            {popularTests.map((pkg, index) => (
                                <div key={index} className={`relative bg-white rounded-2xl shadow-sm border-2 ${pkg.borderColor} overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1`}>
                                    <div className={`${pkg.color} p-6 border-b ${pkg.borderColor}`}>
                                        <h3 className="text-xl font-bold mb-2 flex items-center gap-3">
                                            <i className={`fas ${pkg.icon}`}></i>
                                            {pkg.title || pkg.name}
                                        </h3>
                                        {pkg.testsCount && <p className="font-medium opacity-80">{pkg.testsCount} Parameters Included</p>}
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-baseline gap-2 mb-6">
                                            <span className="text-3xl font-black text-gray-800">{pkg.price}</span>
                                            {pkg.originalPrice && <span className="text-sm text-gray-400 line-through">{pkg.originalPrice}</span>}
                                        </div>
                                        
                                        {pkg.testsList && (
                                            <div className="mb-6">
                                                <p className="text-sm font-semibold text-gray-700 mb-1">Includes:</p>
                                                <p className="text-sm text-gray-600 leading-relaxed">
                                                    {pkg.testsList.split(' | ').join(', ')}
                                                </p>
                                            </div>
                                        )}
                                        
                                        <button 
                                            onClick={() => {
                                                const testToAdd = {
                                                    _id: 'pop_' + index,
                                                    productName: pkg.name,
                                                    mrp: pkg.price.replace('₹', ''),
                                                    packSize: '1 Test',
                                                    imageUrl: 'https://cdn-icons-png.flaticon.com/512/912/912300.png'
                                                };
                                                if (addToCart) addToCart(testToAdd);
                                                if (showMessage) showMessage(`${pkg.name} added to cart!`);
                                            }}
                                            className={`w-full py-3 rounded-xl font-bold border-2 transition-colors ${(pkg.color || '').includes('indigo') ? 'bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600 hover:border-indigo-700' : 'bg-white text-gray-800 hover:bg-gray-50 ' + pkg.borderColor}`}>
                                            Book Test
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Health Packages Section */}
                {comprehensivePackages.length > 0 && (
                    <div className="mb-10 sm:mb-16">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">Comprehensive Health Packages</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                            {comprehensivePackages.map((pkg, index) => (
                                <div key={index} className={`relative bg-white rounded-2xl shadow-sm border-2 ${pkg.borderColor} overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1`}>
                                    {pkg.popular && (
                                        <div className="absolute top-0 right-0 bg-indigo-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg z-10">
                                            MOST POPULAR
                                        </div>
                                    )}
                                    <div className={`${pkg.color} p-6 border-b ${pkg.borderColor}`}>
                                        <h3 className="text-xl font-bold mb-2">{pkg.title}</h3>
                                        <p className="font-medium opacity-80">{pkg.testsCount} Parameters Included</p>
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-baseline gap-2 mb-6">
                                            <span className="text-3xl font-black text-gray-800">{pkg.price}</span>
                                            <span className="text-sm text-gray-400 line-through">{pkg.originalPrice}</span>
                                        </div>
                                        
                                        <div className="mb-8">
                                            <p className="text-sm font-semibold text-gray-700 mb-2">Includes:</p>
                                            <ul className="space-y-2 text-sm text-gray-600">
                                                {pkg.testsList.split(' | ').map((tItem, iItem) => (
                                                    <li key={iItem} className="flex items-start gap-2">
                                                        <i className="fas fa-check-circle text-green-500 mt-1 shrink-0"></i> 
                                                        <span>{tItem}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        
                                        <button 
                                            onClick={() => {
                                                const testToAdd = {
                                                    _id: 'comp_' + index,
                                                    productName: pkg.testName,
                                                    mrp: pkg.price.replace('₹', ''),
                                                    packSize: '1 Test',
                                                    imageUrl: 'https://cdn-icons-png.flaticon.com/512/912/912300.png'
                                                };
                                                if (addToCart) addToCart(testToAdd);
                                                if (showMessage) showMessage(`${pkg.testName} added to cart!`);
                                            }}
                                            className={`w-full py-3 rounded-xl font-bold border-2 transition-colors ${pkg.color.includes('indigo') ? 'bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600 hover:border-indigo-700' : 'bg-white text-gray-800 hover:bg-gray-50 ' + pkg.borderColor}`}>
                                            Book Package
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default LabsView;
